<template>
    <b-modal id="feedbackModal">
        <template v-slot:modal-title>
            <div class="d-flex align-items-center">
                <font-awesome-icon class="fa-2x color-brand mr-2" :icon="['fas', 'comment-smile']" />
                <span class="color-brand modal-title">{{ $t('feedback.give_feedback') }}</span>
            </div>
        </template>
        <template>
            <label for="select-problem">{{ $t('feedback.pick_a_problem') }}</label>
            <b-form-select
                id="select-problem"
                class="mb-4 mt-2"
                v-model="selectedProblem"
                :options="problemOptions"
                @input="error = null"/>
            <VideoTextInput v-model="feedback" @focus="error = null" class="mt-2 w-100 mx-auto">
                <template v-slot:text>{{ $t('feedback.give_problem_text') }}</template>
                <template v-slot:video>{{ $t('feedback.give_problem_vgt') }}</template>
            </VideoTextInput>
            <label for="email">{{ $t('feedback.optional_email_address') }}</label>
            <b-form-input
                id="email"
                v-model="email"
                type="email"
                class="mt-2 mb-4"
                @focus="error = null"
                :placeholder="$t('feedback.email_address_placeholder')"
            />
            <b-alert class="d-flex justify-content-center mt-2" variant="danger" :show="!!error">
                {{ error }}
            </b-alert>
            <i18n class="font-italic feedback-modal__privacy-policy" path="feedback.privacy_statement.feedback"
                ><a target="_blank" :href="privacyPolicyUrl">{{ $t('feedback.privacy_policy') }}</a>
            </i18n>
        </template>
        <template v-slot:modal-footer>
            <div :class="{ 'blurred-unclickable': isSendingFeedback }" class="d-flex">
                <button @click="sendFeedback()" class="vgtc-button mr-4">
                    <font-awesome-icon class="vgtc-button__icon" :icon="['fal', 'paper-plane']" />
                    {{ $t('feedback.send') }}
                </button>
                <button @click="hideModal()" class="vgtc-button vgtc-button--invert">
                    {{ $t('feedback.cancel') }}
                </button>
            </div>
            <b-spinner class="position-absolute m-auto send-feedback-spinner" v-if="isSendingFeedback"></b-spinner>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { Constants } from '../../shared/Constants';
import { doorbellService } from '../../shared/services/DoorbellService';
import { feedbackService } from '../../shared/services/FeedbackService';
import VideoTextInput from './VideoTextInput.vue';

@Component({ components: { VideoTextInput } })
export default class FeedbackModal extends Vue {
    public readonly faqUrl = Constants.faqUrl;
    public readonly privacyPolicyUrl = Constants.privacyPolicyUrl;
    public selectedProblem: number | null = null;

    public feedback: {
        textualFeedback?: string;
        feedbackVideo?: File | Blob;
        textInputActive: boolean;
        videoInputActive: boolean;
    } = { textInputActive: true, videoInputActive: false };

    public email: string = '';
    public error: string | null = null;

    public rating: number = 0;

    public isSendingFeedback: boolean = false;
    
    public get problemOptions(): Array<{ text: string; value: number | null }> {
        return [
            { text: this.$t('feedback.problem.select_one').toString(), value: null },
            { text: this.$t('feedback.problem.video_contains_error').toString(), value: 1 },
            { text: this.$t('feedback.problem.mistake_in_handshapes').toString(), value: 2 },
            { text: this.$t('feedback.problem.mistake_in_locations').toString(), value: 3 },
            { text: this.$t('feedback.problem.mistake_in_categories').toString(), value: 4 },
            { text: this.$t('feedback.problem.mistake_in_variants').toString(), value: 5 },
            { text: this.$t('feedback.problem.video_unclear').toString(), value: 6 },
            { text: this.$t('feedback.problem.video_not_working').toString(), value: 7 },
            { text: this.$t('feedback.problem.video_etymology_unclear').toString(), value: 8 },
            { text: this.$t('feedback.problem.video_etymology_not_working').toString(), value: 9 },
            { text: this.$t('feedback.problem.incorrect_assigned_labels').toString(), value: 10 },
            { text: this.$t('feedback.problem.other_problem').toString(), value: 11 },
            { text: this.$t('feedback.problem.suggestion').toString(), value: 12 },
        ];
    }

    private get canSendFeedback(): boolean {
        const hasText =
            this.feedback.textInputActive &&
            this.feedback.textualFeedback &&
            this.feedback.textualFeedback.trim() !== '';
        const hasVideo =
            this.feedback.videoInputActive &&
            this.feedback.feedbackVideo !== undefined &&
            this.feedback.feedbackVideo !== null;

        return hasText || hasVideo;
    }

    public showModal(): void {
        this.$bvModal.show('feedbackModal');
    }

    public hideModal(): void {
        this.error = null;
        this.$bvModal.hide('feedbackModal');
    }

    public async sendFeedback(): Promise<void> {
        try {
            this.isSendingFeedback = true;
            this.error = null;
            if (!this.canSendFeedback) {
                this.error = this.$t('feedback.problem_or_text_required').toString();
                this.isSendingFeedback = false;
                return;
            }

            await feedbackService.giveGeneralFeedback(
                this.feedback.textInputActive ? this.feedback.textualFeedback : undefined,
                this.feedback.videoInputActive ? this.feedback.feedbackVideo : undefined,
                (this.feedback.videoInputActive &&
                    this.feedback.feedbackVideo &&
                    (this.feedback.feedbackVideo as File).name) ||
                    undefined,
                this.email,
                this.getSelectedProblemText(),
            );
            this.hideModal();
            this.clearModal();
        } catch (err) {
            if (err === doorbellService.INVALID_EMAIL_ERROR) {
                this.error = this.$t('feedback.invalid_email').toString();
            } else if ((err as Error).message === 'Request failed with status code 400') {
                this.error = this.$t('feedback.wrong_video_format').toString();
            } else {
                this.error = this.$t('feedback.something_went_wrong').toString();
            }
        } finally {
            this.isSendingFeedback = false;
        }
    }

    private getSelectedProblemText(): string | undefined {
        if (!this.selectedProblem) {
            return undefined;
        }
        return this.problemOptions[this.selectedProblem].text;
    }

    private clearModal(): void {
        this.email = '';
        this.feedback = { textInputActive: true, videoInputActive: false };
        this.error = null;
    }
}
</script>

<style lang="scss">
.feedback-modal__privacy-policy {
    font-size: $font-size-small;
}

.send-feedback-spinner {
    color: $accent-color;
    left: 0;
    right: 0;
}
</style>
